import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import {
    Button,
    CircularProgress,
    FormLabel,
    IconButton,
    MenuItem,
    Radio,
    RadioGroup,
    FormControlLabel,
    Select,
    TextField,
    InputAdornment
} from "@mui/material";
import { RootState, useAppDispatch, useAppSelector } from "../../../state/state.tsx";
import { closeModal, toggleLoading } from "../../../state/ModalSlice/ModalSlice.ts";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { selectEffectiveUID } from '../../../state/UserStatesV2/userSliceV2.ts';
import { collection, getDocs, doc, getDoc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../../../Config/firebase.ts';
import { SubscriptionType } from '../../../enums.ts';

interface Subscription {
    id: string;
    Name: string;
    subscriptionType: string;
    cyclePriceArray: any[];
    Price?: number;
}

export default function MoveStudentModal() {
    const Dispatch = useAppDispatch();
    const open = useAppSelector((state: RootState) => state.ModalSlice.MoveStudentModalOpen);
    const { LiveSubscriptionId, currentSubscriptionType } = useAppSelector(
        (state: RootState) => state.ArbitrarySlice.ForMoveStudentModal
    );
    const ServiceProviderID = useAppSelector(selectEffectiveUID);
    const isLoading = useAppSelector((state: RootState) => state.ModalSlice.isLoading);

    const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
    const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<string>("");
    const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
    const [newPrice, setNewPrice] = useState<string>("");
    const [selectedPlanIndex, setSelectedPlanIndex] = useState<number>(0);
    const [studentName, setStudentName] = useState<string>("");
    const [currentBatchName, setCurrentBatchName] = useState<string>("");
    const [currentSubscriptionId, setCurrentSubscriptionId] = useState<string>("");
    const [oldSubscriptionId, setOldSubscriptionId] = useState<string>("");

    // Fetch student details
    useEffect(() => {
        if (open && LiveSubscriptionId && ServiceProviderID) {
            fetchStudentDetails();
            fetchSubscriptions();
        }
    }, [open, LiveSubscriptionId, ServiceProviderID]);

    const fetchStudentDetails = async () => {
        try {
            const liveSubRef = doc(db, `ServiceProviders/${ServiceProviderID}/LiveSubscriptions`, LiveSubscriptionId);
            const liveSubDoc = await getDoc(liveSubRef);
            
            if (liveSubDoc.exists()) {
                const data = liveSubDoc.data();
                setStudentName(data.studentName || "");
                setCurrentBatchName(data.subscriptionName || "");
                
                // Get subscription ID from the reference
                let subId = "";
                if (data.subscriptionPlanRef && data.subscriptionPlanRef.id) {
                    subId = data.subscriptionPlanRef.id;
                } else if (data.subscriptionPlanRefId) {
                    subId = data.subscriptionPlanRefId;
                }
                
                setCurrentSubscriptionId(subId);
                setOldSubscriptionId(subId);
            }
        } catch (error) {
            console.error("Error fetching student details:", error);
        }
    };

    const fetchSubscriptions = async () => {
        try {
            const subscriptionsRef = collection(db, `ServiceProviders/${ServiceProviderID}/Subscriptions`);
            const subscriptionsSnap = await getDocs(subscriptionsRef);

            const subs: Subscription[] = [];
            subscriptionsSnap.forEach((docSnap) => {
                const data = docSnap.data();
                // Filter: same subscription type AND not the current batch
                if (data.subscriptionType === currentSubscriptionType && docSnap.id !== currentSubscriptionId) {
                    subs.push({
                        id: docSnap.id,
                        Name: data.Name || data.name || "Unnamed Subscription",
                        subscriptionType: data.subscriptionType,
                        cyclePriceArray: data.cyclePriceArray || [],
                        Price: data.Price
                    });
                }
            });

            setSubscriptions(subs);
        } catch (error) {
            console.error("Error fetching subscriptions:", error);
            toast.error("Failed to load subscriptions");
        }
    };

    const handleSubscriptionChange = (subscriptionId: string) => {
        setSelectedSubscriptionId(subscriptionId);
        const sub = subscriptions.find(s => s.id === subscriptionId);
        setSelectedSubscription(sub || null);
        setSelectedPlanIndex(0);
        
        // Set default price
        if (sub) {
            if (sub.subscriptionType === SubscriptionType.Monthly) {
                setNewPrice(sub.Price?.toString() || "");
            } else if (sub.cyclePriceArray && sub.cyclePriceArray.length > 0) {
                const firstPlan = sub.cyclePriceArray[0];
                setNewPrice(firstPlan.price?.toString() || firstPlan.Price?.toString() || "");
            }
        }
    };

    const handlePlanChange = (index: number) => {
        setSelectedPlanIndex(index);
        if (selectedSubscription && selectedSubscription.cyclePriceArray[index]) {
            const plan = selectedSubscription.cyclePriceArray[index];
            setNewPrice(plan.price?.toString() || plan.Price?.toString() || "");
        }
    };

    const handleClose = () => {
        resetState();
        Dispatch(closeModal("MoveStudentModalOpen"));
    };

    const resetState = () => {
        setSubscriptions([]);
        setSelectedSubscriptionId("");
        setSelectedSubscription(null);
        setNewPrice("");
        setSelectedPlanIndex(0);
        setStudentName("");
        setCurrentBatchName("");
        setCurrentSubscriptionId("");
        setOldSubscriptionId("");
    };

    const validateForm = () => {
        if (!selectedSubscriptionId) {
            toast.error("Please select a batch");
            return false;
        }

        if (!newPrice || parseFloat(newPrice) < 0) {
            toast.error("Please enter a valid price");
            return false;
        }

        return true;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) return;

        Dispatch(toggleLoading(true));

        try {
            if (!selectedSubscription) {
                toast.error("Please select a batch");
                return;
            }

            // Reference to the live subscription document
            const liveSubRef = doc(db, `ServiceProviders/${ServiceProviderID}/LiveSubscriptions`, LiveSubscriptionId);
            
            // Reference to the new subscription
            const newSubRef = doc(db, `ServiceProviders/${ServiceProviderID}/Subscriptions`, selectedSubscriptionId);

            // Base update data for all subscription types
            const updateData: any = {
                Price: parseFloat(newPrice),
                subscriptionName: selectedSubscription.Name,
                subscriptionPlanRefId: selectedSubscriptionId,
                subscriptionPlanRef: newSubRef
            };

            // Add additional fields for MultiMonth and Count subscriptions
            if (
                selectedSubscription.subscriptionType === SubscriptionType.MultiMonth ||
                selectedSubscription.subscriptionType === SubscriptionType.Count
            ) {
                updateData.assignedPlanIndex = selectedPlanIndex;
                updateData.assignedPlans = selectedSubscription.cyclePriceArray;
                
                // Set billing cycle
                if (selectedSubscription.subscriptionType === SubscriptionType.Count) {
                    updateData.billingCycle = "Count";
                } else {
                    const selectedPlan = selectedSubscription.cyclePriceArray[selectedPlanIndex];
                    updateData.billingCycle = selectedPlan.billingCycle;
                }
            }

            // Update the live subscription document
            await updateDoc(liveSubRef, updateData);
           
            
            // Update activeSubscribersCount in both old and new subscription documents
            if (oldSubscriptionId && oldSubscriptionId !== selectedSubscriptionId) {
                console.log("Updating activeSubscribersCount in both old and new subscription documents");
                
                // Decrease count in old subscription
                const oldSubRef = doc(db, `ServiceProviders/${ServiceProviderID}/Subscriptions`, oldSubscriptionId);
                await updateDoc(oldSubRef, {
                    activeSubscribersCount: increment(-1)
                });

                // Increase count in new subscription
                const newSubDocRef = doc(db, `ServiceProviders/${ServiceProviderID}/Subscriptions`, selectedSubscriptionId);
                await updateDoc(newSubDocRef, {
                    activeSubscribersCount: increment(1)
                });
            }
            
            toast.success("Student moved successfully!");
            handleClose();
            window.location.reload();
            
        } catch (error: any) {
            console.error("Error moving student:", error);
            toast.error(error.message || "Failed to move student");
        } finally {
            Dispatch(toggleLoading(false));
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                style: {
                    borderRadius: '12px',
                    padding: '8px'
                }
            }}
        >
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">Move Student</h2>
                <IconButton onClick={handleClose} size="small">
                    <CloseIcon />
                </IconButton>
            </div>

            <DialogContent sx={{ px: 3, py: 3 }}>
                <form onSubmit={handleSubmit}>
                    <div className="space-y-5 py-2">
                        {/* Student Info */}
                        <div className="bg-blue-50 p-3 rounded-lg">
                            <p className="text-sm text-gray-600">Student Name</p>
                            <p className="text-base font-semibold text-gray-800">{studentName}</p>
                            <p className="text-xs text-gray-500 mt-1">Current Batch: {currentBatchName}</p>
                        </div>

                        {/* Select New Batch */}
                        <div>
                            <FormLabel sx={{ mb: 1, fontWeight: 400, fontSize: '16px' }}>
                                Select New Batch *
                            </FormLabel>
                            <Select
                                value={selectedSubscriptionId}
                                onChange={(e) => handleSubscriptionChange(e.target.value)}
                                displayEmpty
                                required
                                size="small"
                                fullWidth
                                sx={{
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'grey',
                                        borderRadius: '10px'
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'grey',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'skyblue',
                                    },
                                    '& .MuiSelect-select': {
                                        fontSize: '14px',
                                    },
                                }}
                            >
                                <MenuItem value="" disabled sx={{ fontSize: '14px' }}>
                                    Select a batch
                                </MenuItem>
                                {subscriptions.length === 0 ? (
                                    <MenuItem value="" disabled sx={{ fontSize: '14px' }}>
                                        No other batches available
                                    </MenuItem>
                                ) : (
                                    subscriptions.map((sub) => (
                                        <MenuItem key={sub.id} value={sub.id} sx={{ fontSize: '14px' }}>
                                            {sub.Name}
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                        </div>

                        {/* Plan Selection for MultiMonth and Count */}
                        {selectedSubscription &&
                            (selectedSubscription.subscriptionType === SubscriptionType.MultiMonth ||
                            selectedSubscription.subscriptionType === SubscriptionType.Count) &&
                            selectedSubscription.cyclePriceArray &&
                            selectedSubscription.cyclePriceArray.length > 0 && (
                            <div>
                                <FormLabel sx={{ mb: 1, fontWeight: 400, fontSize: '16px' }}>
                                    Select Plan *
                                </FormLabel>
                                <RadioGroup
                                    value={selectedPlanIndex}
                                    onChange={(e) => handlePlanChange(Number(e.target.value))}
                                >
                                    {selectedSubscription.cyclePriceArray.map((plan, index) => (
                                        <FormControlLabel
                                            key={index}
                                            value={index}
                                            control={<Radio />}
                                            label={
                                                selectedSubscription.subscriptionType === SubscriptionType.Count
                                                    ? `${plan.count || plan.Count} classes - ${plan.validity || plan.Validity} days - ₹${plan.price || plan.Price}`
                                                    : `${plan.billingCycle} (${plan.validity} days) - ₹${plan.price}`
                                            }
                                            sx={{
                                                border: '1px solid #e5e7eb',
                                                borderRadius: '8px',
                                                padding: '8px 12px',
                                                marginBottom: '8px',
                                                '&:hover': {
                                                    backgroundColor: '#f9fafb',
                                                },
                                            }}
                                        />
                                    ))}
                                </RadioGroup>
                            </div>
                        )}

                        {/* New Price */}
                        <div>
                            <FormLabel sx={{ mb: 1, fontWeight: 400, fontSize: '16px' }}>
                                New Price *
                            </FormLabel>
                            <TextField
                                type="number"
                                value={newPrice}
                                onChange={(e) => setNewPrice(e.target.value)}
                                placeholder="Enter price"
                                required
                                size="small"
                                inputProps={{ min: 0, step: "0.01" }}
                                fullWidth
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: 'grey',
                                            borderRadius: '10px'
                                        },
                                        '&:hover fieldset': {
                                            borderColor: 'grey',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: 'skyblue',
                                        },
                                        '& .MuiInputBase-input': {
                                            fontSize: '14px',
                                        },
                                    },
                                    fontWeight: 400,
                                    fontSize: "1px",
                                }}
                            />
                        </div>

                        {/* Warning Message */}
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                            <p className="text-sm text-yellow-800">
                                <strong>Note:</strong> Moving the student will update their batch assignment
                                and pricing details. This action cannot be undone.
                            </p>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-3 pt-5 mt-5 border-t border-gray-200">
                        <Button
                            onClick={handleClose}
                            variant="outlined"
                            disabled={isLoading}
                            sx={{
                                textTransform: 'none',
                                px: 3,
                                py: 1,
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={isLoading}
                            sx={{
                                textTransform: 'none',
                                px: 3,
                                py: 1,
                                backgroundColor: '#7c3aed',
                                '&:hover': {
                                    backgroundColor: '#6d28d9',
                                },
                                '&.Mui-disabled': {
                                    backgroundColor: '#d1d5db',
                                },
                            }}
                        >
                            {isLoading ? (
                                <>
                                    <CircularProgress size={20} style={{ marginRight: '8px', color: 'white' }} />
                                    Moving...
                                </>
                            ) : (
                                'Move Student'
                            )}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}

